/**
 * New rule · Edit rule · Duplicate rule — one drawer, three numbered cards
 * (Where · When · Floor and ceiling), **nothing saved until Save**
 * (CU-869fkbjdh § 3.4, UX rethink § 3.3–3.5).
 *
 * The two choices of the model are two radios: WHEN the dates are read
 * (pickup between · any rental up to 30 days that crosses · any date) and
 * WHERE the floor comes from (one amount · a matrix). The row the drawer
 * saves is the same `repricing_price_limit` shape the collection has
 * always held — the drawer is a different way to fill it, not a new
 * entity.
 *
 * Saving is a direct call (`setupApi.priceLimits.create` / `update`, the
 * pattern of `useProducts`), so the server's 409 `price_limit_duplicate`
 * lands inside the drawer; the tab then adopts the saved row into the
 * collection (`sync.adopt`) and no diff ever creates it a second time.
 * Enter in Floor or Ceiling saves; Escape closes (the drawer's own).
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
	Box, Button, FormControlLabel, Link, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { AppFormDrawer, AppFormSection, AppNote } from '@components'
import { dsColorNew } from '@palette'
import { OpenInNewOutlined as OpenInNewIcon } from 'Pages/DsRepricing/@components/icons'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import { futurePresets } from 'Pages/DsRepricing/@services/dateRange'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { SectionLabel } from 'Pages/DsRepricing/Setup/@components/Surface'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { setupApi, useAxisCatalog, PREPAID_OPTIONS, prepaidKey, prepaidValue, idKey, idValue } from 'Pages/DsRepricing/Setup/@services'
import { priceLimits as limitAdapter, priceMatrices } from 'Pages/DsRepricing/Setup/@services/adapters'
import { describeApiError } from 'Pages/DsRepricing/Setup/@services/useSetupCollection'
import {
	readsAs, unprotectedBands, isBinding, fmtMoney, MAX_PROTECTED_DAYS,
} from 'Pages/DsRepricing/Setup/@services/priceLimitsProjection'
import { OfficeScopePicker, CategoryScopePicker, DurationScopePicker, EnumScopeSelect } from './pickers'
import { matrixHref } from './cells'

export const BLANK_RULE = {
	scope_ps_provider_id: null, scope_is_prepaid: null, scope_ps_insurance_id: null,
	scope_office_group: null, scope_offices: null, scope_categories: null, scope_duration: null,
	date_from: null, date_to: null, protects_dates: false, name: null, matrix_id: null,
	min_price: null, max_price: null,
}

const TITLES = { new: 'New rule', edit: 'Edit rule', duplicate: 'Duplicate rule' }
const ICONS = { new: 'plus', edit: 'pen', duplicate: 'clone' }

/**
 * `mode`: 'new' | 'edit' | 'duplicate' · `initial`: the row (edit / duplicate)
 * · `rules`: every row, for the duplicate pre-check · `onSaved(row)`: the
 * server's row, already in the client shape · `onOpenRule(id)`.
 */
export default function RuleDrawer({ open, mode, initial, rules, matrices, catalog, onSaved, onClose, onOpenRule }) {
	if (!open) return null
	// Keyed so "open that rule" from the duplicate note remounts the body with the other row.
	return <DrawerBody key={`${mode}:${initial?.id ?? 'new'}`} mode={mode} initial={initial} rules={rules} matrices={matrices}
	                   catalog={catalog} onSaved={onSaved} onClose={onClose} onOpenRule={onOpenRule}/>
}

function DrawerBody({ mode, initial, rules, matrices, catalog, onSaved, onClose, onOpenRule }) {
	const axes = useAxisCatalog()
	const [draft, setDraft] = useState(() => {
		const base = { ...BLANK_RULE, ...(initial || {}) }
		// A copy carries everything except the identity; a new one has none
		// until the server answers (criterion 15).
		if (mode !== 'edit') { delete base.id; delete base.specificity }
		return base
	})
	const [saving, setSaving] = useState(false)
	const [serverError, setServerError] = useState(null)        // { duplicate: bool, message }
	const patch = (p) => { setDraft(d => ({ ...d, ...p })); setServerError(null) }

	// The two readings of the dates, as one radio.
	const [whenChoice, setWhenChoice] = useState(
		draft.protects_dates ? 'crosses' : (draft.date_from || draft.date_to) ? 'pickup' : 'any')
	function chooseWhen(v) {
		setWhenChoice(v)
		if (v === 'crosses') patch({ protects_dates: true, max_price: null })
		else if (v === 'pickup') patch({ protects_dates: false, name: null })
		else patch({ protects_dates: false, name: null, date_from: null, date_to: null })
	}
	const floorMode = isBinding(draft) ? 'matrix' : 'amount'
	function chooseFloor(v) {
		if (v === 'matrix') patch({ matrix_id: draft.matrix_id || (matrices[0]?.id ?? ''), min_price: null, max_price: null,
		                            scope_categories: null, scope_duration: null })
		else patch({ matrix_id: null })
	}
	const matrix = isBinding(draft) ? matrices.find(m => String(m.id) === String(draft.matrix_id)) || null : null
	const matrixById = useMemo(() => new Map(matrices.map(m => [String(m.id), m])), [matrices])

	// "it prices N other scopes" — from the server, read-only.
	const [usage, setUsage] = useState(null)
	useEffect(() => {
		let alive = true
		setUsage(null)
		if (!matrix?.id) return undefined
		setupApi.matrixUsage(matrix.id)
			.then(s => {
				if (!alive) return
				const n = Array.isArray(s) ? s.length : Number(s) || 0
				const own = mode === 'edit' && String(initial?.matrix_id) === String(matrix.id) ? 1 : 0
				setUsage(Math.max(0, n - own))
			})
			.catch(() => { if (alive) setUsage(null) })
		return () => { alive = false }
	}, [matrix?.id])

	// What blocks Save, in the operator's words.
	const hasDates = !!(draft.date_from && draft.date_to)
	const blocker = whenChoice === 'crosses' && !hasDates ? 'A protection needs the two dates it protects.'
		: whenChoice === 'crosses' && !(draft.name || '').trim() ? 'Name the protection — the Activity will say it.'
		: floorMode === 'matrix' && !matrix ? 'Pick the matrix the floors come from.'
		: floorMode === 'amount' && draft.min_price == null && draft.max_price == null ? 'Set a floor, a ceiling, or both.'
		: floorMode === 'amount' && draft.min_price != null && draft.max_price != null && draft.max_price < draft.min_price ? 'The ceiling is under the floor.'
		: null
	// The server's own duplicate rule — scope and dates, not the name (TL obs. 10) — checked before Save.
	const duplicate = useMemo(() => {
		const key = limitAdapter.duplicateKey(normalised(draft, whenChoice, floorMode))
		return rules.find(r => String(r.id) !== String(draft.id) && limitAdapter.duplicateKey(r) === key) || null
	}, [draft, rules, whenChoice, floorMode])
	const duplicateShown = duplicate || (serverError?.duplicate ? serverError.match || true : null)

	const reads = readsAs(normalised(draft, whenChoice, floorMode), catalog.labels, matrix)
	const warn = unprotectedBands(draft, matrix)
	const matrixOnly = floorMode === 'matrix'
	const canSave = !blocker && !duplicate && !serverError?.duplicate && !saving

	async function save() {
		if (!canSave) return
		const row = normalised(draft, whenChoice, floorMode)
		const dto = limitAdapter.toServer(row)
		setSaving(true)
		try {
			const res = mode === 'edit'
				? await setupApi.priceLimits.update(row.id, dto)
				: await setupApi.priceLimits.create(dto)
			// `update` answers the row too; a 204 would answer null — then the draft is the truth.
			const saved = limitAdapter.fromServer(res && res.id != null ? res : { ...dto, id: row.id, specificity: row.specificity })
			onSaved(saved)
		} catch (e) {
			// The server's judgement stays in the drawer: a duplicate it saw
			// and the client did not (the match is the local row with the
			// same key — the 409 carries no `existing_id`), or anything else
			// with its own words, Save enabled to retry.
			if (e?.name === 'ApiError' && e.code === 'price_limit_duplicate') {
				const key = limitAdapter.duplicateKey(row)
				const match = rules.find(r => String(r.id) !== String(row.id) && limitAdapter.duplicateKey(r) === key) || null
				setServerError({ duplicate: true, match, message: describeApiError(e) })
			} else {
				setServerError({ duplicate: false, message: describeApiError(e) })
			}
		} finally {
			setSaving(false)
		}
	}
	const onAmountKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); save() } }

	const subtitle = mode === 'edit' ? 'Scope, dates and where the floor comes from · amounts also edit in the list'
		: mode === 'duplicate' ? `A copy of “${readsAs(initial || BLANK_RULE, catalog.labels, matrixById.get(String(initial?.matrix_id)))}” — nothing exists until you press Save`
		: 'Where it applies, when the dates are read, where the floor comes from'

	const footer = (
		<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
			<Typography sx={{ fontSize: 12, color: duplicateShown ? dsColorNew.dangerRed : dsColorNew.textSubtle }}>
				{duplicateShown ? 'Not saved.' : blocker ? blocker
				 : <>Nothing is saved until you press Save · <Kbd>Enter</Kbd> in an amount saves · <Kbd>Esc</Kbd> cancels</>}
			</Typography>
			<Stack direction="row" gap={1}>
				<Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 500, color: dsColorNew.textMuted,
				                               '&:hover': { bgcolor: dsColorNew.pillTrackBg } }}>Cancel</Button>
				<Button variant="contained" onClick={save} disabled={!canSave} title={blocker || undefined}
				        sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', bgcolor: dsColorNew.appBlue,
				              '&:hover': { bgcolor: dsColorNew.appBlueHover, boxShadow: 'none' } }}>
					{saving ? 'Saving…' : 'Save rule'}
				</Button>
			</Stack>
		</Stack>
	)

	return (
		<AppFormDrawer isOpen controller={onClose} width={720} icon={ICONS[mode] || 'pen'}
		               title={TITLES[mode] || 'Rule'} subtitle={subtitle} footer={footer}
		               containerProps={{ PaperProps: { sx: setupScopeSx } }}>
			<Box sx={{ px: 1.5, py: 1, bgcolor: dsColorNew.white, border: `1px dashed ${dsColorNew.grayBorder}`,
			           borderRadius: '10px', fontSize: 13, color: dsColorNew.textMuted }}>
				Reads as: <Box component="strong" sx={{ color: dsColorNew.textTitle, fontWeight: 600 }}>{reads}</Box>
			</Box>

			{duplicateShown && (
				<AppNote severity="error">
					<strong>A rule with this scope and these dates already exists</strong>
					{duplicateShown !== true && <> — “{readsAs(duplicateShown, catalog.labels, matrixById.get(String(duplicateShown.matrix_id)))}”</>}
					{' — '}
					{duplicateShown !== true ? (
						<Link component="button" type="button" onClick={() => onOpenRule(duplicateShown.id)}
						      sx={{ color: 'inherit', fontWeight: 600, verticalAlign: 'baseline', fontSize: 'inherit' }}>
							open that rule
						</Link>
					) : 'open that rule'}
					{' '}to change its amounts, or make this one more specific.
				</AppNote>
			)}
			{serverError && !serverError.duplicate && (
				<AppNote severity="error">{serverError.message}</AppNote>
			)}

			<AppFormSection title={<Numbered n={1}>Where</Numbered>}
			                hint="empty = any · the more you fill, the more specific — and the more specific wins">
				<Stack gap={2}>
					<Labelled label="Offices">
						<OfficeScopePicker rule={draft} onChange={patch}/>
					</Labelled>
					<Stack direction="row" gap={2}>
						<Labelled label="Categories (ACRISS or macro)" sx={{ flex: 1, minWidth: 0 }}>
							<CategoryScopePicker value={draft.scope_categories} disabled={matrixOnly}
							                     onChange={(v) => patch({ scope_categories: v })}/>
						</Labelled>
						<Labelled label="Rental length" sx={{ flex: 1, minWidth: 0 }}>
							<DurationScopePicker value={draft.scope_duration} disabled={matrixOnly}
							                     onChange={(v) => patch({ scope_duration: v })}/>
						</Labelled>
					</Stack>
					<Stack direction="row" gap={2}>
						<Labelled label="Franchise" sx={{ flex: 1, minWidth: 0 }}>
							<EnumScopeSelect value={idKey(draft.scope_ps_provider_id)} options={axes.franchises}
							                 allLabel="Any franchise" variant="franchise"
							                 onChange={(v) => patch({ scope_ps_provider_id: idValue(v) })}/>
						</Labelled>
						<Labelled label="Tariff type" sx={{ flex: 1, minWidth: 0 }}>
							<EnumScopeSelect value={prepaidKey(draft.scope_is_prepaid)} options={PREPAID_OPTIONS}
							                 allLabel="Any tariff type" variant="tariff"
							                 onChange={(v) => patch({ scope_is_prepaid: prepaidValue(v) })}/>
						</Labelled>
						<Labelled label="Insurance" sx={{ flex: 1, minWidth: 0 }}>
							<EnumScopeSelect value={idKey(draft.scope_ps_insurance_id)} options={axes.insurances}
							                 allLabel="Any insurance" variant="insurance"
							                 onChange={(v) => patch({ scope_ps_insurance_id: idValue(v) })}/>
						</Labelled>
					</Stack>
				</Stack>
			</AppFormSection>

			<AppFormSection title={<Numbered n={2}>When</Numbered>} hint="two readings of the same two dates — pick the one you mean">
				<RadioGroup value={whenChoice} onChange={(e) => chooseWhen(e.target.value)}>
					<Choice value="pickup" title="Pickup between the dates" hint="— the usual rule; competes on specificity"/>
					{whenChoice === 'pickup' && (
						<Box sx={{ pl: 4, pb: 1 }}>
							<DateRangeField from={draft.date_from} to={draft.date_to} cleanable presets={futurePresets()}
							                placeholder="Pick the dates" width={300}
							                onChange={({ from, to }) => patch({ date_from: from, date_to: to })}/>
						</Box>
					)}
					<Choice value="crosses" title={`Any rental up to ${MAX_PROTECTED_DAYS} days that crosses the dates`}
					        hint="— a date protection; added on top, highest floor wins" accent/>
					{whenChoice === 'crosses' && (
						<Stack gap={1.5} sx={{ pl: 4, pb: 1 }}>
							<DateRangeField from={draft.date_from} to={draft.date_to} cleanable presets={futurePresets()}
							                placeholder="Pick the dates it protects" width={300}
							                onChange={({ from, to }) => patch({ date_from: from, date_to: to })}/>
							{!hasDates && <Typography sx={{ fontSize: 12, color: dsColorNew.dangerRed, mt: -1 }}>A protection needs the dates it protects.</Typography>}
							<Labelled label="Name — the Activity says “Date protection · New Year”">
								<TextField size="small" value={draft.name ?? ''} placeholder="e.g. Christmas"
								           onChange={(e) => patch({ name: e.target.value || null })}
								           sx={{ width: 300, ...inputSx }}/>
							</Labelled>
							<AppNote severity="info">
								The whole rental is floored, not only the days inside. A rental that does not cross these
								dates is untouched. Takes effect on the next run over the cell.
							</AppNote>
						</Stack>
					)}
					{/* "Any date" is hidden while "crosses" is chosen: a protection without dates protects nothing */}
					{whenChoice !== 'crosses' && <Choice value="any" title="Any date"/>}
				</RadioGroup>
			</AppFormSection>

			<AppFormSection title={<Numbered n={3}>{whenChoice === 'crosses' ? 'Floor' : 'Floor and ceiling'}</Numbered>}
			                hint={whenChoice === 'crosses' ? 'shelf prices, €/day · a protection has no ceiling' : 'shelf prices, €/day'}>
				<RadioGroup value={floorMode} onChange={(e) => chooseFloor(e.target.value)}>
					<Choice value="amount" title="One amount"/>
					{floorMode === 'amount' && (
						<Stack direction="row" gap={2} sx={{ pl: 4, pb: 1 }}>
							<Labelled label="Floor">
								<AmountInput value={draft.min_price} onChange={(v) => patch({ min_price: v })} onKeyDown={onAmountKey}
								             aria="Floor"/>
							</Labelled>
							{whenChoice !== 'crosses' && (
								<Labelled label="Ceiling (optional)">
									<AmountInput value={draft.max_price} onChange={(v) => patch({ max_price: v })} onKeyDown={onAmountKey}
									             aria="Ceiling"/>
								</Labelled>
							)}
						</Stack>
					)}
					<Choice value="matrix" title="From a matrix" hint="— a floor per ACRISS and rental length" disabled={!matrices.length}/>
					{floorMode === 'matrix' && (
						<Stack gap={1.5} sx={{ pl: 4, pb: 1 }}>
							<Labelled label="Matrix">
								<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
									<Select size="small" value={matrix ? String(matrix.id) : ''} displayEmpty
									        onChange={(e) => patch({ matrix_id: e.target.value })}
									        sx={{ minWidth: 260, ...inputSx }}>
										<MenuItem value="" disabled>Pick a matrix…</MenuItem>
										{matrices.map(m => <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>)}
									</Select>
									{matrix && (
										<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
											<Link component={RouterLink} to={matrixHref(matrix.id)}
											      sx={{ color: dsColorNew.appBlue, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5,
											            textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
												Open it in Price matrix <OpenInNewIcon sx={{ fontSize: 12 }}/>
											</Link>
											{usage != null && <> · it prices {usage} other scope{usage === 1 ? '' : 's'}</>}
										</Typography>
									)}
								</Stack>
							</Labelled>
							{matrix && <MatrixPreview matrix={matrix}/>}
							{warn && <AppNote severity="warning">{warn.text}</AppNote>}
						</Stack>
					)}
				</RadioGroup>
			</AppFormSection>
		</AppFormDrawer>
	)
}

/** The row as it will be saved: what the radios say wins over leftovers of the other choice. */
function normalised(draft, whenChoice, floorMode) {
	const row = { ...draft }
	if (whenChoice !== 'crosses') { row.protects_dates = false; row.name = null }
	if (whenChoice === 'any') { row.date_from = null; row.date_to = null }
	if (floorMode === 'matrix') { row.min_price = null; row.max_price = null; row.scope_categories = null; row.scope_duration = null }
	else row.matrix_id = null
	if (row.protects_dates) row.max_price = null
	return row
}

function Kbd({ children }) {
	return (
		<Box component="kbd" sx={{ font: 'inherit', fontSize: 11, fontWeight: 500, color: dsColorNew.textMuted, px: 0.75, py: '1px',
		                           borderRadius: '5px', bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderBottomWidth: 2 }}>
			{children}
		</Box>
	)
}

/** The number of the step in the same soft tile the sections give their icon. */
function Numbered({ n, children }) {
	return (
		<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
			<Box component="span" sx={{ width: 28, height: 28, borderRadius: '8px', flexShrink: 0, fontSize: 12, fontWeight: 600,
			                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			                            bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>
				{n}
			</Box>
			{children}
		</Box>
	)
}

function Labelled({ label, children, sx }) {
	return (
		<Stack gap={0.75} sx={sx}>
			<SectionLabel>{label}</SectionLabel>
			{children}
		</Stack>
	)
}

function Choice({ value, title, hint, accent, disabled }) {
	return (
		<FormControlLabel value={value} disabled={disabled}
		                  control={<Radio size="small" sx={accent ? { '&.Mui-checked': { color: dsColorNew.noteInfo } } : undefined}/>}
		                  label={<Typography sx={{ fontSize: 14, color: dsColorNew.textTitle }}>
		                      <strong style={{ fontWeight: 600 }}>{title}</strong>
		                      {hint && <Box component="span" sx={{ color: dsColorNew.textMuted, fontSize: 13 }}> {hint}</Box>}
		                  </Typography>}/>
	)
}

function AmountInput({ value, onChange, onKeyDown, aria }) {
	return (
		<TextField size="small" type="number" value={value ?? ''} placeholder="—"
		           onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
		           onKeyDown={onKeyDown}
		           inputProps={{ min: 0, step: '0.5', 'aria-label': aria, style: { width: 90, textAlign: 'right', fontWeight: 600 } }}
		           InputProps={{ endAdornment: <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, ml: 0.5 }}>€/day</Typography> }}
		           sx={inputSx}/>
	)
}

/** The floors the matrix sets — ACRISS × bands — so the operator sees what is being bound before Save (criterion 12). */
function MatrixPreview({ matrix }) {
	const bands = matrix.base || []
	const acriss = [matrix.base_acriss, ...(matrix.rules || []).map(r => r.acriss)].filter(Boolean)
	const shown = acriss.slice(0, 4)
	const label = (b) => (b.to == null ? `${b.from ?? 1}+ d` : b.from === b.to ? `${b.from} d` : `${b.from ?? 1}–${b.to} d`)
	const mid = (b) => (b.to == null ? (b.from ?? 1) : Math.floor(((b.from ?? 1) + b.to) / 2))
	const cell = { fontSize: 12.5, py: 0.75, px: 1.25, borderBottom: `1px solid ${dsColorNew.grayBorder}`, whiteSpace: 'nowrap',
	               fontVariantNumeric: 'tabular-nums' }
	return (
		<Box aria-label="Floors this matrix sets" sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', overflow: 'auto', bgcolor: dsColorNew.white }}>
			<Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
				<thead>
					<tr>
						<Box component="th" sx={{ ...cell, textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
						                           letterSpacing: 0.5, color: dsColorNew.textMuted, bgcolor: dsColorNew.tableRowAlt }}>ACRISS</Box>
						{bands.map((b, i) => (
							<Box key={i} component="th" sx={{ ...cell, textAlign: 'right', fontWeight: 600, fontSize: 11, color: dsColorNew.textMuted,
							                                   bgcolor: dsColorNew.tableRowAlt }}>{label(b)}</Box>
						))}
					</tr>
				</thead>
				<tbody>
					{shown.map(a => (
						<tr key={a}>
							<Box component="td" sx={{ ...cell, fontWeight: 600 }}>
								{a}{a === matrix.base_acriss && <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: dsColorNew.textSubtle }}>ref.</Box>}
							</Box>
							{bands.map((b, i) => (
								<Box key={i} component="td" sx={{ ...cell, textAlign: 'right' }}>
									{fmtMoney(priceMatrices.floorFor(matrix, a, mid(b)))}
								</Box>
							))}
						</tr>
					))}
					{acriss.length > shown.length && (
						<tr><Box component="td" colSpan={bands.length + 1} sx={{ ...cell, color: dsColorNew.textSubtle, fontStyle: 'italic', borderBottom: 'none' }}>
							… {acriss.length - shown.length} more ACRISS
						</Box></tr>
					)}
				</tbody>
			</Box>
		</Box>
	)
}
